/**
 * Flowlytics Pixel SDK - Cookieless Tracking Fallback
 *
 * When third-party cookies are blocked (Safari ITP, Firefox ETP, Chrome Privacy Sandbox):
 * 1. Detect cookie availability
 * 2. Fall back to server-set first-party cookie via pixel endpoint
 * 3. Use fingerprint-based identity as last resort
 * 4. Probabilistic matching for cross-session identity
 *
 * Created: 2026-02-08 (Phase 1, Week 4 — Pixel SDK Hardening)
 */

const COOKIELESS_STORAGE_KEY = "flw_cl_id";
const SERVER_COOKIE_NAME = "_flw_sid";

class CookielessTracker {
  constructor(config) {
    this.config = config;
    this._cookiesAvailable = null;
    this._clientId = null;
  }

  /**
   * Initialize cookieless tracking.
   * Detects cookie support and sets up fallback identity.
   */
  init() {
    this._cookiesAvailable = this._detectCookieSupport();

    // Get or create client ID (survives cookie blocks)
    this._clientId = this._getOrCreateClientId();

    if (this.config.debug) {
      console.log(
        "[Flowlytics Cookieless] Cookies available:",
        this._cookiesAvailable,
      );
      console.log("[Flowlytics Cookieless] Client ID:", this._clientId);
    }
  }

  /**
   * Get the best available client identifier.
   * Priority: server-set cookie > localStorage ID > fingerprint
   */
  getClientId() {
    return this._clientId;
  }

  /**
   * Whether cookies are available in this browser context.
   */
  areCookiesAvailable() {
    return this._cookiesAvailable;
  }

  /**
   * Get tracking identity data for event payloads.
   * Returns the identity method used and the client ID.
   */
  getIdentity(fingerprint) {
    const identity = {
      client_id: this._clientId,
      method: "unknown",
      cookies_available: this._cookiesAvailable,
    };

    // Determine which method provided the identity
    const serverCookie = this._readCookie(SERVER_COOKIE_NAME);
    if (serverCookie) {
      identity.method = "server_cookie";
      identity.client_id = serverCookie;
    } else if (this._clientId && this._clientId.startsWith("flw_")) {
      identity.method = "local_storage";
    } else if (fingerprint) {
      identity.method = "fingerprint";
      identity.client_id = fingerprint;
    }

    return identity;
  }

  /**
   * Request a server-set first-party cookie.
   * The backend sets an HttpOnly cookie in the response.
   * This survives ITP/ETP because it's a first-party server-set cookie.
   */
  async requestServerCookie() {
    try {
      const endpoint = this.config.endpoint.replace(
        "/api/pixel/events",
        "/api/pixel/identity",
      );

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Important: include cookies in response
        body: JSON.stringify({
          api_key: this.config.apiKey,
          client_id: this._clientId,
          request_server_cookie: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.client_id) {
          this._clientId = data.client_id;
          this._persistClientId(data.client_id);
        }

        if (this.config.debug) {
          console.log(
            "[Flowlytics Cookieless] Server cookie requested:",
            data.client_id,
          );
        }
      }
    } catch (e) {
      if (this.config.debug) {
        console.warn(
          "[Flowlytics Cookieless] Server cookie request failed:",
          e.message,
        );
      }
    }
  }

  /**
   * Probabilistic matching score.
   * Combines multiple weak signals to estimate identity match probability.
   *
   * @param {Object} signals - { ip, userAgent, screenRes, timezone, language }
   * @returns {number} 0-1 probability score
   */
  calculateMatchScore(signals) {
    let score = 0;
    let maxScore = 0;

    // IP address match (strong signal)
    if (signals.ip) {
      maxScore += 40;
      if (signals.previousIp === signals.ip) score += 40;
    }

    // User-Agent match (medium signal)
    if (signals.userAgent) {
      maxScore += 20;
      if (signals.previousUA === signals.userAgent) score += 20;
    }

    // Screen resolution match (weak signal)
    if (signals.screenRes) {
      maxScore += 10;
      if (signals.previousScreen === signals.screenRes) score += 10;
    }

    // Timezone match (weak signal)
    if (signals.timezone) {
      maxScore += 10;
      if (signals.previousTZ === signals.timezone) score += 10;
    }

    // Language match (weak signal)
    if (signals.language) {
      maxScore += 10;
      if (signals.previousLang === signals.language) score += 10;
    }

    // Accept-Language + platform match (medium signal)
    if (signals.platform) {
      maxScore += 10;
      if (signals.previousPlatform === signals.platform) score += 10;
    }

    return maxScore > 0 ? score / maxScore : 0;
  }

  /**
   * Detect if cookies are available.
   */
  _detectCookieSupport() {
    try {
      // Try to set a test cookie
      document.cookie = "_flw_test=1;SameSite=Lax;path=/";
      const canSet = document.cookie.includes("_flw_test=1");

      // Clean up test cookie
      if (canSet) {
        document.cookie =
          "_flw_test=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      }

      return canSet;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get or create a persistent client ID.
   * Tries: server cookie → localStorage → generate new UUID.
   * New IDs are held in memory only — call persistIfConsented() after analytics consent.
   */
  _getOrCreateClientId() {
    // 1. Check server-set cookie
    const serverCookie = this._readCookie(SERVER_COOKIE_NAME);
    if (serverCookie) return serverCookie;

    // 2. Check localStorage (set by a previous consented session)
    try {
      const stored = localStorage.getItem(COOKIELESS_STORAGE_KEY);
      if (stored) return stored;
    } catch (e) {
      // localStorage unavailable
    }

    // 3. Generate new in-memory ID — do NOT persist yet (requires analytics consent)
    return "flw_" + this._generateUUID();
  }

  /**
   * Persist client ID to localStorage after analytics consent is granted.
   * Safe to call multiple times — no-op if ID was already persisted.
   */
  persistIfConsented(analyticsGranted) {
    if (
      analyticsGranted &&
      this._clientId &&
      this._clientId.startsWith("flw_")
    ) {
      this._persistClientId(this._clientId);
    }
  }

  /**
   * Persist client ID to localStorage.
   */
  _persistClientId(clientId) {
    try {
      localStorage.setItem(COOKIELESS_STORAGE_KEY, clientId);
    } catch (e) {
      // localStorage unavailable
    }
  }

  /**
   * Read a cookie by name.
   */
  _readCookie(name) {
    try {
      const match = document.cookie.match(
        new RegExp("(^| )" + name + "=([^;]+)"),
      );
      return match ? decodeURIComponent(match[2]) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Generate a UUID v4.
   */
  _generateUUID() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback for older browsers
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export default CookielessTracker;
